import type {
    CanisterClientInterface,
    EAOrganization,
    EAApplicationEntity,
    EATechnicalService,
    EABusinessServiceEntity,
    EADataObject,
    EAHRRole,
    EARelationship,
} from '../canister-client';

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export interface ImpactReport {
    entity: { type: string; id: string; name: string };
    directImpact: {
        applicationsAffected: number;
        servicesAffected: number;
        infrastructureAffected: number;
        dataObjectsAffected: number;
    };
    downstreamImpact: {
        totalHeadcountAffected: number;
        divisionsAffected: string[];
        processesDisrupted: number;
        slasAtRisk: Array<{ service: string; sla: string }>;
        dataEntitiesAtRisk: Array<{ name: string; classification: string }>;
    };
    riskLevel: 'Critical' | 'High' | 'Medium' | 'Low';
    affectedEntities: AffectedEntity[];
}

export interface AffectedEntity {
    id: string;
    name: string;
    type: string;
    relationship: string;
    hopDistance: number; // 1 = direct, 2 = indirect
}

// ---------------------------------------------------------------------------
// Internal helper type for the entity lookup map
// ---------------------------------------------------------------------------

interface EntityDescriptor {
    name: string;
    type: string;
}

// ---------------------------------------------------------------------------
// Risk-level thresholds (centralised so they are easy to tune)
// ---------------------------------------------------------------------------

const HEADCOUNT_CRITICAL = 500;
const HEADCOUNT_HIGH = 200;
const HEADCOUNT_MEDIUM = 50;

const APPS_HIGH = 3;
const APPS_MEDIUM = 1;

const SLAS_HIGH = 2;
const SLAS_MEDIUM = 1;

const PII_CLASSIFICATIONS = new Set(['pii', 'confidential', 'restricted']);

// ---------------------------------------------------------------------------
// EAImpactAnalyzer
// ---------------------------------------------------------------------------

export class EAImpactAnalyzer {
    constructor(private canisterClient: CanisterClientInterface) {}

    /**
     * Analyse the downstream impact of changes to a specific entity.
     * Traverses the relationship graph up to 2 hops.
     */
    async analyzeImpact(entityType: string, entityId: string): Promise<ImpactReport> {
        // 1. Fetch all EA data in parallel
        const [
            organizations,
            applications,
            technicalServices,
            businessServices,
            dataObjects,
            hrRoles,
            relationships,
        ] = await Promise.all([
            this.canisterClient.listEAOrganizations(),
            this.canisterClient.listEAApplications(),
            this.canisterClient.listEATechnicalServices(),
            this.canisterClient.listEABusinessServices(),
            this.canisterClient.listEADataObjects(),
            this.canisterClient.listEAHRRoles(),
            this.canisterClient.listEARelationships(),
        ]);

        // 2. Build a lookup map for every entity keyed by its id
        const entityMap = this.buildEntityMap(
            organizations,
            applications,
            technicalServices,
            businessServices,
            dataObjects,
            hrRoles,
        );

        // 3. Resolve the source entity name
        const entityName = entityMap.get(entityId)?.name ?? entityId;

        // 4. Find all direct relationships (hop 1)
        const directRels = relationships.filter(
            (r) => r.source_id === entityId || r.target_id === entityId,
        );

        // 5. Collect all directly-affected entity ids
        const directIds = new Set<string>();
        for (const rel of directRels) {
            directIds.add(rel.source_id === entityId ? rel.target_id : rel.source_id);
        }

        // 6. Find indirect relationships (hop 2)
        const indirectIds = new Set<string>();
        for (const directId of directIds) {
            const hop2Rels = relationships.filter(
                (r) =>
                    (r.source_id === directId || r.target_id === directId) &&
                    r.source_id !== entityId &&
                    r.target_id !== entityId,
            );
            for (const rel of hop2Rels) {
                const otherId = rel.source_id === directId ? rel.target_id : rel.source_id;
                if (!directIds.has(otherId) && otherId !== entityId) {
                    indirectIds.add(otherId);
                }
            }
        }

        // 7. Build affected-entities list
        const affectedEntities: AffectedEntity[] = [];

        for (const id of directIds) {
            const descriptor = entityMap.get(id);
            if (descriptor) {
                const rel = directRels.find((r) => r.source_id === id || r.target_id === id);
                affectedEntities.push({
                    id,
                    name: descriptor.name,
                    type: descriptor.type,
                    relationship: rel?.relationship_type ?? 'related',
                    hopDistance: 1,
                });
            }
        }

        for (const id of indirectIds) {
            const descriptor = entityMap.get(id);
            if (descriptor) {
                affectedEntities.push({
                    id,
                    name: descriptor.name,
                    type: descriptor.type,
                    relationship: 'indirect',
                    hopDistance: 2,
                });
            }
        }

        // 8. Count direct impacts by entity type
        const directImpact = {
            applicationsAffected: affectedEntities.filter((e) => e.type === 'application').length,
            servicesAffected: affectedEntities.filter(
                (e) => e.type === 'business_service' || e.type === 'technical_service',
            ).length,
            infrastructureAffected: 0, // Infrastructure comes from device discovery, not EA
            dataObjectsAffected: affectedEntities.filter((e) => e.type === 'data_object').length,
        };

        // 9. Calculate downstream impact
        const allAffectedIds = new Set([...directIds, ...indirectIds]);

        // Collect application domains that are affected
        const affectedAppDomains = new Set<string>();
        for (const id of allAffectedIds) {
            const app = applications.find((a) => a.app_id === id);
            if (app) {
                affectedAppDomains.add(app.domain.toLowerCase());
            }
        }

        // Determine which divisions and headcount are impacted
        const divisionsAffected = new Set<string>();
        let totalHeadcount = 0;
        for (const org of organizations) {
            if (affectedAppDomains.has(org.division.toLowerCase()) || allAffectedIds.has(org.org_id)) {
                divisionsAffected.add(org.division);
                totalHeadcount += org.headcount;
            }
        }

        // Also include headcount from HR roles whose systems overlap with affected apps
        for (const role of hrRoles) {
            if (allAffectedIds.has(role.role_id)) {
                totalHeadcount += role.headcount;
                divisionsAffected.add(role.division);
            }
        }

        // SLAs at risk from technical and business services
        const slasAtRisk: Array<{ service: string; sla: string }> = [];
        for (const svc of technicalServices) {
            if (allAffectedIds.has(svc.service_id)) {
                slasAtRisk.push({ service: svc.name, sla: svc.availability_sla });
            }
        }
        for (const svc of businessServices) {
            if (allAffectedIds.has(svc.service_id)) {
                slasAtRisk.push({ service: svc.business_service, sla: svc.sla });
            }
        }

        // Data entities at risk
        const dataAtRisk: Array<{ name: string; classification: string }> = [];
        for (const obj of dataObjects) {
            if (allAffectedIds.has(obj.data_object_id)) {
                dataAtRisk.push({ name: obj.name, classification: obj.classification });
            }
        }

        // 10. Calculate risk level
        const riskLevel = this.calculateRiskLevel(
            affectedEntities,
            applications,
            allAffectedIds,
            totalHeadcount,
            slasAtRisk,
            dataAtRisk,
        );

        return {
            entity: { type: entityType, id: entityId, name: entityName },
            directImpact,
            downstreamImpact: {
                totalHeadcountAffected: totalHeadcount,
                divisionsAffected: Array.from(divisionsAffected),
                processesDisrupted: 0, // Can be enhanced with process-flow data
                slasAtRisk,
                dataEntitiesAtRisk: dataAtRisk,
            },
            riskLevel,
            affectedEntities,
        };
    }

    // -----------------------------------------------------------------------
    // Private helpers
    // -----------------------------------------------------------------------

    /**
     * Build a unified lookup map across all EA entity types.
     */
    private buildEntityMap(
        organizations: EAOrganization[],
        applications: EAApplicationEntity[],
        technicalServices: EATechnicalService[],
        businessServices: EABusinessServiceEntity[],
        dataObjects: EADataObject[],
        hrRoles: EAHRRole[],
    ): Map<string, EntityDescriptor> {
        const map = new Map<string, EntityDescriptor>();

        for (const org of organizations) {
            map.set(org.org_id, { name: `${org.division} - ${org.department}`, type: 'organization' });
        }
        for (const app of applications) {
            map.set(app.app_id, { name: app.name, type: 'application' });
        }
        for (const svc of technicalServices) {
            map.set(svc.service_id, { name: svc.name, type: 'technical_service' });
        }
        for (const svc of businessServices) {
            map.set(svc.service_id, { name: svc.business_service, type: 'business_service' });
        }
        for (const obj of dataObjects) {
            map.set(obj.data_object_id, { name: obj.name, type: 'data_object' });
        }
        for (const role of hrRoles) {
            map.set(role.role_id, { name: role.role, type: 'hr_role' });
        }

        return map;
    }

    /**
     * Determine the overall risk level of the impact.
     *
     * Thresholds (evaluated top-down, first match wins):
     *   Critical -- any critical-rated application affected, OR headcount > 500,
     *               OR PII / confidential / restricted data at risk
     *   High     -- > 3 applications affected, OR headcount > 200, OR 2+ SLAs at risk
     *   Medium   -- > 1 application affected, OR headcount > 50, OR 1 SLA at risk
     *   Low      -- everything else
     */
    private calculateRiskLevel(
        affectedEntities: AffectedEntity[],
        applications: EAApplicationEntity[],
        allAffectedIds: Set<string>,
        totalHeadcount: number,
        slasAtRisk: Array<{ service: string; sla: string }>,
        dataAtRisk: Array<{ name: string; classification: string }>,
    ): 'Critical' | 'High' | 'Medium' | 'Low' {
        // Gather the set of affected application entities
        const affectedApps = applications.filter((a) => allAffectedIds.has(a.app_id));
        const affectedAppCount = affectedApps.length;

        // --- Critical checks ---
        const hasCriticalApp = affectedApps.some(
            (a) => a.criticality.toLowerCase() === 'critical',
        );
        const hasPiiData = dataAtRisk.some((d) =>
            PII_CLASSIFICATIONS.has(d.classification.toLowerCase()),
        );

        if (hasCriticalApp || totalHeadcount > HEADCOUNT_CRITICAL || hasPiiData) {
            return 'Critical';
        }

        // --- High checks ---
        if (
            affectedAppCount > APPS_HIGH ||
            totalHeadcount > HEADCOUNT_HIGH ||
            slasAtRisk.length >= SLAS_HIGH
        ) {
            return 'High';
        }

        // --- Medium checks ---
        if (
            affectedAppCount > APPS_MEDIUM ||
            totalHeadcount > HEADCOUNT_MEDIUM ||
            slasAtRisk.length >= SLAS_MEDIUM
        ) {
            return 'Medium';
        }

        return 'Low';
    }
}
